/** Rows for the Dashboard "Coming up" list. Tasks are read directly; every
 *  dated project obligation comes from buildProjectDeadlines, the same source
 *  the Calendar flags use, so the two surfaces cannot disagree about what is
 *  due (payment notice and pay less cut-off used to appear only on the
 *  Calendar). Pure so the ordering and windowing can be tested. */

import type { Project, ContractItem, InterimApplication } from '../../types/database';
import type { Task } from '../../types/tasks';
import type { ProjectDeadlineKind } from '../../types/calendar';
import type { ExpiringWorkerCard } from '../../types/workerCards';
import type { PlantAsset } from '../../types/plant';
import { coverTypeLabel, type ExpiringSubcontractorCover } from '../subcontractors/subcontractorCover';
import type { CISReturn, ExpenseClaim } from '../../types/accounting';
import { cisDueCue, cisReturnDueDate } from '../cis/cisDueDate';
import { daysWaiting } from '../expenses/expenseWaiting';
import { PENDING_FLAG_DAYS } from '../interim/variationPending';
import { buildChaseList } from '../interim/chaseList';
import { splitByChase } from '../interim/chaseLog';
import type { InterimChaseEvent } from '../../types/interimChase';
import { rfiDaysOverdue, localDateKey } from '../templates/rfiRegister';
import type { RfiRecord } from '../../types/rfis';
import { enquiryReplyStatus } from '../projects/procurement/enquiryReply';
import { purchaseOrderDeliveryStatus } from '../projects/procurement/purchaseOrderDelivery';
import { buildProjectDeadlines, parseDeadlineDate } from '../../utils/projectDeadlines';
import { normalizeProjectStage } from '../../utils/stageUtils';
import { safeParse } from './utils';
import { buildEnquiryTenderRows } from './enquiryTenderRows';
import type { WorkspaceDeal } from '../pipeline/types';
import type { Invoice } from '../../types/accounting';
import { slowDayworkRow } from './slowDayworkRow';
import type { UnsignedDayworkTally } from '../../contract-administration/dayworkSignOff';
import { overdueInvoiceRow, type InvoiceChaseStamp } from './overdueInvoiceRow';

/** Structural twin of the snagging service's tally; importing the service would drag the web-only Supabase client into the native type-check. */
export interface SnagTally { open: number; fixed: number }

export type ActionTarget =
  | { view: 'project-detail'; projectId: string; projectName: string; projectTab?: string }
  | { view: 'calendar'; taskId: string }
  | { view: 'payroll-employee'; employeeId: string }
  | { view: 'assets'; assetId?: string }
  | { view: 'cis' }
  | { view: 'sales'; invoiceFilter?: 'overdue' }
  | { view: 'payroll-subcontractors' }
  | { view: 'workspace-pipeline' }
  | { view: 'purchases'; section: 'expenses' };

export interface ActionItem {
  id: string;
  title: string;
  context: string;
  due: Date | null;
  overdue: boolean;
  target: ActionTarget | null;
}

// Milestones such as practical completion are dates to plan around, not things
// to do, so they stay out of a "do now" list unless the user asks for them.
const MILESTONE_KINDS = new Set<ProjectDeadlineKind>([
  'start_on_site',
  'practical_completion',
]);

const ACTIONABLE_KINDS = new Set<ProjectDeadlineKind>([
  'tender_return',
  'interim_valuation',
  'payment_notice',
  'pay_less_notice',
  'final_payment',
  'contract_item',
]);

// buildProjectDeadlines rejects years before 2000, so this reaches every
// overdue item it will ever return, as the list did before.
const EARLIEST_OVERDUE = new Date(2000, 0, 1);

// An unsent CIS return stays owed and draws a penalty, so it outlives the
// one-day lapse limit above; a draft older than this is a forgotten record.
const OVERDUE_CIS_DAYS = 90;

// A submitted claim is nudged on Home once it has waited this long; the row's
// "Nd over" counts from 1 on the day it is first shown. The Expenses list itself flags from 3 days.
export const EXPENSE_HOME_WAIT_DAYS = 7;

// Retention is money owed to the company, so the end of the defects period is
// an action rather than a milestone and is shown further ahead than the
// 7 day list. A project still in its defects period long after the end date
// is a stale record, not something to chase, so lapsed rows stop after 90 days.
const RETENTION_WINDOW_DAYS = 30;
const RETENTION_LAPSED_DAYS = 90;

// The paged site card endpoint starts at yesterday, so that is as far back as
// a lapsed card can be listed; anything older stays with the Employees banner,
// which never forgets one.
const EXPIRED_CARD_DAYS = 1;
// A lapsed policy stays a "do now" for longer than a card: the firm may be on site uninsured until it is renewed.
const EXPIRED_COVER_DAYS = 30;

// A merchant that has not answered a month after the return-by date is not
// going to; the Procurement tab keeps flagging it, Home stops nagging.
const ENQUIRY_LAPSED_DAYS = 30;

// An RFI still unanswered three months after its response date is a forgotten
// record, not a live chase; the RFI Register keeps flagging it, Home stops.
const RFI_LAPSED_DAYS = 90;

// A submitted tender with no word after three weeks is worth a call to the
// client. A bid still undecided after a further 90 days is a dead enquiry,
// which the Enquiries board is the place to close, so Home stops showing it.
export const TENDER_CHASE_DAYS = 21;
const TENDER_LAPSED_DAYS = 90;

interface BuildActionItemsInput {
  tasks: Task[];
  contractItems: ContractItem[];
  applications: InterimApplication[];
  projects: Project[];
  /** Workspace admins only; the server refuses everyone else, so callers pass nothing. */
  siteCards?: readonly ExpiringWorkerCard[];
  /** Active subcontractor policies near or past their end; the caller passes them only to workspace admins, the only people the server answers. */
  subcontractorCover?: readonly ExpiringSubcontractorCover[];
  /** Machines needing a LOLER exam; the caller passes only what the user may read. */
  plantAssets?: readonly PlantAsset[];
  /** The workspace's CIS returns; the caller passes them only to roles that may open CIS Returns. */
  cisReturns?: readonly CISReturn[];
  /** Submitted claims awaiting approval; the caller passes them only to workspace admins, the people who can approve. */
  expenseClaims?: readonly ExpenseClaim[];
  /** Outstanding snag tallies by project; a retention release row names the snags still to sign off before the money is released. */
  outstandingSnags?: Readonly<Record<string, SnagTally>>;
  /** Unsigned daywork tallies by project from `GET /dayworks/unsigned`; only projects with a sheet waiting past the slow threshold produce a row. */
  unsignedDayworks?: Readonly<Record<string, UnsignedDayworkTally>>;
  /** The chase log for projects with pending variations; a variation chased recently drops off Home. */
  chaseEvents?: readonly InterimChaseEvent[];
  /** Unpaid customer invoices; the caller passes them only to people who may open the Sales register. */
  salesInvoices?: readonly Invoice[];
  /** Newest chase per customer invoice; an invoice chased in the last few days drops off Home. */
  invoiceChases?: readonly InvoiceChaseStamp[];
  /** Projects whose Procurement tab the user may open (pricing access). Fails closed: with no set, or a project outside it, no enquiry row is added. */
  enquiryProjectIds?: ReadonlySet<string>;
  /** The issued RFIs the caller may read; the caller passes them only to roles that may open the RFI Register. */
  rfis?: readonly RfiRecord[];
  /** The Enquiries-board deals the caller may read; the caller passes them only to people holding the Enquiries board grant. */
  enquiryDeals?: readonly Pick<WorkspaceDeal, 'id' | 'title' | 'client_name' | 'stage' | 'tender_deadline'>[];
  windowDays: number;
  includeMilestones?: boolean;
  /** Field-only members: tasks and site milestones only, no tender,
   *  payment, contract item or retention dates. */
  siteOnly?: boolean;
  now?: Date;
}

/**
 * Projects whose Procurement tab this user may open. The server strips
 * `settings.procurement` from every project the caller lacks pricing access
 * on, so its presence is the permission; nothing here widens what the user sees.
 */
export function projectsWithProcurementAccess(projects: readonly Project[]): ReadonlySet<string> {
  return new Set(
    projects
      .filter((p) => {
        const procurement = p.settings?.procurement;
        return typeof procurement === 'object' && procurement !== null;
      })
      .map((p) => p.id),
  );
}

/** Whole calendar days from `from` to `to`; 24-hour steps are wrong across a clock change. */
export function calendarDaysBetween(from: Date, to: Date): number {
  return Math.round(
    (Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) - Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) / 86400000,
  );
}

/** Open and fixed-but-unsigned snags both still stand in the way of releasing retention. */
function outstandingSnagCount(tally: SnagTally | undefined): number {
  return tally ? tally.open + tally.fixed : 0;
}

export function buildActionItems({
  tasks,
  contractItems,
  applications,
  projects,
  siteCards = [],
  subcontractorCover = [],
  plantAssets = [],
  cisReturns = [],
  expenseClaims = [],
  chaseEvents = [],
  salesInvoices = [],
  invoiceChases = [],
  unsignedDayworks = {},
  outstandingSnags,
  enquiryProjectIds,
  rfis = [],
  enquiryDeals = [],
  windowDays,
  includeMilestones = false,
  siteOnly = false,
  now = new Date(),
}: BuildActionItemsInput): ActionItem[] {
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const horizon = today0 + windowDays * 86400000;
  const day = (offset: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset).getTime();
  const retentionHorizon = day(Math.max(windowDays, RETENTION_WINDOW_DAYS));
  const projectName = (id?: string) => projects.find((p) => p.id === id)?.name ?? '—';
  const items: ActionItem[] = [];

  tasks.forEach((t) => {
    if (t.status === 'completed' || t.status === 'cancelled') return;
    const d = safeParse(t.due_date);
    if (!d) return;
    const dt = d.getTime();
    if (dt > horizon) return;
    items.push({
      id: `task-${t.id}`,
      title: t.title,
      context: projectName(t.project_id),
      due: d,
      overdue: dt < today0,
      target: { view: 'calendar', taskId: t.id },
    });
  });

  buildProjectDeadlines({
    projects,
    applications,
    contractItems,
    rangeStart: EARLIEST_OVERDUE,
    rangeEnd: new Date(retentionHorizon),
  }).forEach((d) => {
    const dt = d.date.getTime();
    if (siteOnly && !MILESTONE_KINDS.has(d.kind)) return;
    if (d.kind === 'defects_liability_end') {
      if (dt > retentionHorizon || dt < day(-RETENTION_LAPSED_DAYS)) return;
    } else {
      if (dt > horizon) return;
      if (!ACTIONABLE_KINDS.has(d.kind) && !(includeMilestones && MILESTONE_KINDS.has(d.kind))) return;
    }
    const outstanding = d.kind === 'defects_liability_end' ? outstandingSnagCount(outstandingSnags?.[d.projectId]) : 0;
    const suffix = ` · ${d.projectName}`;
    const title = d.title.endsWith(suffix) ? d.title.slice(0, -suffix.length) : d.title;
    items.push({
      id: d.id,
      title: d.kind === 'defects_liability_end' ? `${title}: retention release` : title,
      context: outstanding > 0 ? `${d.projectName} · ${outstanding} ${outstanding === 1 ? 'snag' : 'snags'} not signed off` : d.projectName,
      due: d.date,
      overdue: d.date.getTime() < today0,
      target: { view: 'project-detail', projectId: d.projectId, projectName: d.projectName, projectTab: d.projectTab },
    });
  });

  const cardHorizon = new Date(now.getFullYear(), now.getMonth(), now.getDate() + windowDays).getTime();
  siteCards.forEach((c) => {
    const d = parseDeadlineDate(c.expiryDate);
    if (!d) return;
    const dt = d.getTime();
    // Judged against today here, not the server's daysLeft snapshot, so a
    // dashboard left open past midnight re-judges which cards have lapsed.
    // Calendar days, not 24-hour steps: a clock change makes a day 23 or 25 hours.
    if (dt > cardHorizon || dt < new Date(now.getFullYear(), now.getMonth(), now.getDate() - EXPIRED_CARD_DAYS).getTime()) return;
    const name = c.title || c.schemeCode;
    items.push({
      id: `sitecard-${c.id}`,
      title: dt < today0 ? `${name} expired` : `${name} expires`,
      context: c.holderName,
      due: d,
      overdue: dt < today0,
      // A subcontractor's card has no profile to open.
      target: c.holderType === 'employee' ? { view: 'payroll-employee', employeeId: c.holderId } : null,
    });
  });

  // One row per firm, for the policy that ends soonest: a firm with three policies lapsing
  // in the same month is one chase, not three. Judged against today here, as site cards are.
  const soonestCover = new Map<string, { policy: ExpiringSubcontractorCover; date: Date }>();
  subcontractorCover.forEach((policy) => {
    const date = parseDeadlineDate(policy.endDate);
    if (!date) return;
    const held = soonestCover.get(policy.subcontractorId);
    if (!held || date.getTime() < held.date.getTime()) soonestCover.set(policy.subcontractorId, { policy, date });
  });
  const coverLapseLimit = day(-EXPIRED_COVER_DAYS);
  soonestCover.forEach(({ policy, date }) => {
    const dt = date.getTime();
    if (dt > cardHorizon || dt < coverLapseLimit) return;
    const lapsed = dt < today0;
    items.push({
      id: `cover-${policy.subcontractorId}`,
      title: lapsed ? `${coverTypeLabel(policy.coverType)} cover expired` : `${coverTypeLabel(policy.coverType)} cover expires`,
      context: policy.subcontractorName,
      due: date,
      overdue: lapsed,
      target: { view: 'payroll-subcontractors' },
    });
  });

  // Same window and one-day lapse limit as site cards: a machine whose exam
  // lapsed a week ago is a register matter, not a "coming up" row.
  plantAssets.forEach((a) => {
    // Off-hire and quarantined machines are not lifting, so a lapsed exam is no action for now.
    if (!a.lolerRequired || a.status !== 'active') return;
    const d = parseDeadlineDate(a.lolerNextExam);
    if (!d) return;
    const dt = d.getTime();
    if (dt > cardHorizon || dt < new Date(now.getFullYear(), now.getMonth(), now.getDate() - EXPIRED_CARD_DAYS).getTime()) return;
    items.push({
      id: `loler-${a.id}`,
      title: dt < today0 ? `${a.plantNumber} LOLER exam overdue` : `${a.plantNumber} LOLER exam due`,
      context: a.machineType,
      due: d,
      overdue: dt < today0,
      target: { view: 'assets', assetId: a.id },
    });
  });

  cisReturns.forEach((r) => {
    const cue = cisDueCue(r.taxMonth, r.status, now);
    const d = cisReturnDueDate(r.taxMonth);
    if (!cue || !d) return;
    const dt = d.getTime();
    if (dt > cardHorizon || dt < new Date(now.getFullYear(), now.getMonth(), now.getDate() - OVERDUE_CIS_DAYS).getTime()) return;
    items.push({
      id: `cis-${r.id ?? r.taxMonth}`,
      title: cue.overdue ? 'CIS return overdue' : 'CIS return due',
      context: r.contractorName,
      due: d,
      overdue: cue.overdue,
      target: { view: 'cis' },
    });
  });

  expenseClaims.forEach((claim) => {
    const waited = daysWaiting(claim, now);
    if (waited === null || waited < EXPENSE_HOME_WAIT_DAYS || !claim.id) return;
    items.push({
      id: `expense-${claim.id}`,
      title: `Expense claim waiting ${waited} days`,
      context: [claim.claimNumber, claim.description].filter(Boolean).join(' - ') || 'Awaiting approval',
      due: new Date(now.getFullYear(), now.getMonth(), now.getDate() - waited + EXPENSE_HOME_WAIT_DAYS - 1),
      overdue: true,
      target: { view: 'purchases', section: 'expenses' },
    });
  });

  if (!siteOnly) {
    const overdueInvoices = overdueInvoiceRow(salesInvoices, invoiceChases, now);
    if (overdueInvoices) items.push(overdueInvoices);
    const slowDayworks = slowDayworkRow(unsignedDayworks, projects);
    if (slowDayworks) items.push(slowDayworks);
  }

  // One row per project, not per variation: a project with a dozen unanswered
  // variations is one chase. The Chase list owns what counts as pending (drafts
  // and carried-over variations included), so Home and Valuations cannot
  // disagree; variations chased in the last few days are left out here too.
  const { toChase } = splitByChase(
    siteOnly ? [] : buildChaseList(applications, now).filter((i) => i.kind === 'variation'),
    chaseEvents,
    now,
  );
  const pendingByProject = new Map<string, number[]>();
  toChase.forEach((i) => pendingByProject.set(i.projectId, [...(pendingByProject.get(i.projectId) ?? []), i.days]));
  pendingByProject.forEach((days, projectId) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const oldest = Math.max(...days);
    items.push({
      id: `variations-pending-${projectId}`,
      title: days.length === 1 ? `Variation pending ${oldest} days` : `${days.length} variations pending, oldest ${oldest} days`,
      context: project.name,
      due: new Date(now.getFullYear(), now.getMonth(), now.getDate() - oldest + PENDING_FLAG_DAYS),
      overdue: true,
      target: { view: 'project-detail', projectId, projectName: project.name, projectTab: 'interim' },
    });
  });

  // One row per project, like variations: three silent merchants are one
  // chase. Procurement state lives in the project's settings, so this adds no
  // request; only projects in the pricing-access set are read.
  // The row names the merchant only when exactly one is behind it; with several, the project's tab lists them.
  const withMerchant = (projectName: string, names: string[]) => (names.length === 1 && names[0] ? `${projectName} \u00b7 ${names[0]}` : projectName);
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  projects.forEach((project) => {
    if (!enquiryProjectIds?.has(project.id)) return;
    if (project.archiveStatus === 'archived' || project.archiveStatus === 'pending-deletion') return;
    const enquiries = project.settings?.procurement?.enquiries;
    if (!Array.isArray(enquiries)) return;
    let late = 0;
    let oldestLate = 0;
    let soonestDays: number | null = null;
    const lateMerchants: string[] = [];
    let soonMerchants: string[] = [];
    enquiries.forEach((enquiry) => {
      const status = enquiryReplyStatus(enquiry, todayIso);
      if (!status) return;
      if (status.kind === 'overdue') {
        if (status.days > ENQUIRY_LAPSED_DAYS) return;
        late += 1;
        oldestLate = Math.max(oldestLate, status.days);
        lateMerchants.push(enquiry.merchantName);
      } else if (soonestDays === null || status.days < soonestDays) {
        soonestDays = status.days;
        soonMerchants = [enquiry.merchantName];
      } else if (status.days === soonestDays) {
        soonMerchants.push(enquiry.merchantName);
      }
    });
    const waiting = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
    const target: ActionTarget = { view: 'project-detail', projectId: project.id, projectName: project.name, projectTab: 'procurement/enquiries' };
    if (late > 0) {
      items.push({
        id: `enquiry-reply-${project.id}`,
        title: late === 1 ? `Merchant reply overdue by ${waiting(oldestLate)}` : `${late} merchant replies overdue, oldest by ${waiting(oldestLate)}`,
        context: withMerchant(project.name, lateMerchants),
        due: new Date(now.getFullYear(), now.getMonth(), now.getDate() - oldestLate),
        overdue: true,
        target,
      });
    } else if (soonestDays !== null) {
      const days = soonestDays;
      items.push({
        id: `enquiry-reply-${project.id}`,
        title: days === 0 ? 'Merchant reply due today' : `Merchant reply due in ${waiting(days)}`,
        context: withMerchant(project.name, soonMerchants),
        due: new Date(now.getFullYear(), now.getMonth(), now.getDate() + days),
        overdue: false,
        target,
      });
    }
  });

  // A submitted tender nobody has answered. Not on the field-only list, like every other tender date.
  // Native reads raw /database rows, which carry projectStage but no stage.
  // A Won or Lost pipeline status is the decision, even while the stage still says submitted.
  projects.forEach((project) => {
    if (siteOnly || normalizeProjectStage(project.stage ?? project.projectStage) !== 'tender_submitted') return;
    if (project.pipelineStatus === 'won' || project.pipelineStatus === 'lost') return;
    if (project.archiveStatus === 'archived' || project.archiveStatus === 'pending-deletion') return;
    const submitted = safeParse(project.tenderSubmissionDate);
    if (!submitted) return;
    const waited = calendarDaysBetween(submitted, now);
    if (waited < TENDER_CHASE_DAYS || waited > TENDER_CHASE_DAYS + TENDER_LAPSED_DAYS) return;
    items.push({
      id: `tender-decision-${project.id}`,
      title: `No tender decision after ${waited} days`,
      context: project.name,
      due: new Date(submitted.getFullYear(), submitted.getMonth(), submitted.getDate() + TENDER_CHASE_DAYS),
      overdue: true,
      target: { view: 'project-detail', projectId: project.id, projectName: project.name },
    });
  });

  // Same rule for goods: one row per project for orders past their expected delivery date, or
  // due within two days when none is late. Same pricing-access set as the enquiry rows, because
  // the Orders list is part of the Procurement tab.
  projects.forEach((project) => {
    if (!enquiryProjectIds?.has(project.id)) return;
    if (project.archiveStatus === 'archived' || project.archiveStatus === 'pending-deletion') return;
    const orders = project.settings?.procurement?.purchaseOrders;
    if (!Array.isArray(orders)) return;
    let late = 0;
    let oldestLate = 0;
    let soonestDays: number | null = null;
    const lateMerchants: string[] = [];
    let soonMerchants: string[] = [];
    // A single late order is named by its PO number as well as the merchant, so the row says which delivery to chase.
    const orderLabel = (order: { poNumber?: string; merchantName: string }) => [order.poNumber, order.merchantName].filter(Boolean).join(' \u00b7 ');
    orders.forEach((order) => {
      const status = purchaseOrderDeliveryStatus(order, todayIso);
      if (!status) return;
      if (status.kind === 'overdue') {
        late += 1;
        oldestLate = Math.max(oldestLate, status.days);
        lateMerchants.push(orderLabel(order));
      } else if (soonestDays === null || status.days < soonestDays) {
        soonestDays = status.days;
        soonMerchants = [orderLabel(order)];
      } else if (status.days === soonestDays) {
        soonMerchants.push(orderLabel(order));
      }
    });
    const waiting = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;
    const target: ActionTarget = { view: 'project-detail', projectId: project.id, projectName: project.name, projectTab: 'procurement/orders' };
    if (late > 0) {
      items.push({
        id: `po-delivery-${project.id}`,
        title: late === 1 ? `Delivery overdue by ${waiting(oldestLate)}` : `${late} deliveries overdue, oldest by ${waiting(oldestLate)}`,
        context: withMerchant(project.name, lateMerchants),
        due: new Date(now.getFullYear(), now.getMonth(), now.getDate() - oldestLate),
        overdue: true,
        target,
      });
    } else if (soonestDays !== null) {
      const days = soonestDays;
      items.push({
        id: `po-delivery-${project.id}`,
        title: days === 0 ? 'Delivery due today' : `Delivery due in ${waiting(days)}`,
        context: withMerchant(project.name, soonMerchants),
        due: new Date(now.getFullYear(), now.getMonth(), now.getDate() + days),
        overdue: false,
        target,
      });
    }
  });

  // An issued RFI past its response date. One row per project, like the other
  // chases: three late answers are one conversation with the design team.
  // Only projects Home was given count (never archived ones), and field-only
  // members never see design-team chases.
  if (!siteOnly) {
    const rfiToday = localDateKey(now);
    const lateByProject = new Map<string, { days: number; numbers: string[] }>();
    rfis.forEach((rfi) => {
      const days = rfiDaysOverdue(rfi, rfiToday);
      if (days === null || days > RFI_LAPSED_DAYS) return;
      const entry = lateByProject.get(rfi.projectId) ?? { days: 0, numbers: [] };
      entry.days = Math.max(entry.days, days);
      entry.numbers.push(rfi.rfiNumber);
      lateByProject.set(rfi.projectId, entry);
    });
    projects.forEach((project) => {
      const late = lateByProject.get(project.id);
      if (!late) return;
      if (project.archiveStatus === 'archived' || project.archiveStatus === 'pending-deletion') return;
      const count = late.numbers.length;
      const waiting = `${late.days} ${late.days === 1 ? 'day' : 'days'}`;
      items.push({
        id: `rfi-overdue-${project.id}`,
        title: count === 1 ? `RFI overdue by ${waiting}` : `${count} RFIs overdue, oldest by ${waiting}`,
        context: count === 1 && late.numbers[0] ? `${project.name} \u00b7 ${late.numbers[0]}` : project.name,
        due: new Date(now.getFullYear(), now.getMonth(), now.getDate() - late.days),
        overdue: true,
        target: { view: 'project-detail', projectId: project.id, projectName: project.name, projectTab: 'rfis' },
      });
    });
  }

  // A bid still to be returned on the Enquiries board. Not on the field-only list, like every other tender date.
  if (!siteOnly) buildEnquiryTenderRows(enquiryDeals, now, windowDays).forEach((row) => items.push(row));

  return items.sort((a, b) => {
    if (a.overdue && !b.overdue) return -1;
    if (!a.overdue && b.overdue) return 1;
    return (a.due?.getTime() ?? 0) - (b.due?.getTime() ?? 0);
  });
}
